<template>
	<div id="deliverable-detail" class="task-panel">
		<header class="task-panel-header">
			<button
				v-if="navigationStack.length"
				type="button"
				class="icon-action back-action"
				:aria-label="navigationStack[navigationStack.length - 1].deliverable.name"
				:title="navigationStack[navigationStack.length - 1].deliverable.name"
				:disabled="isSaving || isNavigating"
				@click="navigateBack"
			>
				<portal-icon name="arrow-left" :size="18"></portal-icon>
			</button>
			<input
				v-model="deliverable.name"
				class="task-title-input"
				type="text"
				:disabled="isReadOnly"
				:aria-label="$t('projects.deliverable-name')"
				:placeholder="$t('projects.new-deliverable')"
			/>
			<div class="task-panel-actions">
				<button
					v-if="!isReadOnly"
					type="button"
					class="save-action"
					:disabled="isSaving"
					@click="saveAndCloseInternal"
				>
					<v-progress-circular v-if="isSaving" indeterminate :size="14" :width="2"></v-progress-circular>
					<portal-icon v-else name="check" :size="15"></portal-icon>
					{{ $t('global.save') }}
				</button>
				<button
					type="button"
					class="icon-action"
					:aria-label="$t('global.close')"
					:title="$t('global.close')"
					@click.stop="handleClose"
				>
					<portal-icon name="x" :size="19"></portal-icon>
				</button>
			</div>
		</header>

		<div class="task-panel-fixed">
			<p v-if="isProjectSuspended(project)" class="font-14" role="status">{{ $t('projects.lifecycle-read-only') }}</p>
			<div class="project-context">
				<portal-icon name="folder-kanban" :size="15"></portal-icon>
				<span>{{ project.name }}</span>
				<template v-if="navigationStack.length">
					<portal-icon name="chevron-right" :size="12"></portal-icon>
					<span class="parent-context">{{ navigationStack[navigationStack.length - 1].deliverable.name }}</span>
				</template>
			</div>
			<div
				class="task-tabs"
				role="tablist"
				:style="{ gridTemplateColumns: `repeat(${visibleTabCount}, minmax(0, 1fr))` }"
			>
				<button
					type="button"
					role="tab"
					:aria-selected="tabItem === 'overview'"
					:class="{ active: tabItem === 'overview' }"
					@click="tabItem = 'overview'"
				>
					{{ $t('projects.overview.heading') }}
				</button>
				<button
					v-if="panelSubtasks.length"
					type="button"
					role="tab"
					:aria-selected="tabItem === 'subtasks'"
					:class="{ active: tabItem === 'subtasks' }"
					@click="tabItem = 'subtasks'"
				>
					{{ $t('projects.subtasks') }}
					<span class="tab-count">{{ panelSubtasks.length }}</span>
				</button>
				<button
					type="button"
					role="tab"
					:aria-selected="tabItem === 'comments'"
					:class="{ active: tabItem === 'comments' }"
					@click="tabItem = 'comments'"
				>
					{{ $t('projects.feed') }}
				</button>
				<button
					type="button"
					role="tab"
					:aria-selected="tabItem === 'files'"
					:class="{ active: tabItem === 'files' }"
					@click="tabItem = 'files'"
				>
					{{ $t('nav.files') }}
				</button>
			</div>
		</div>

		<main class="task-panel-body show-scrollbar">
			<section v-show="tabItem === 'overview'" class="task-section">
				<div class="meta-grid">
					<label>{{ $t('projects.filter.status') }}</label>
					<v-select
						class="panel-field"
						hide-details
						dense
						solo
						flat
						:disabled="isReadOnly"
						:items="statusList"
						v-model="deliverable.statusId"
						item-text="label"
						item-value="id"
						:menu-props="{ contentClass: 'task-status-menu', offsetY: true }"
					>
						<template slot="selection" slot-scope="{ item }">
							<span class="status-chip" :style="{ '--status-color': item.hexColor }">
								<span></span>{{ item.label }}
							</span>
						</template>
						<template slot="item" slot-scope="{ item }">
							<span class="status-chip" :style="{ '--status-color': item.hexColor }">
								<span></span>{{ item.label }}
							</span>
						</template>
					</v-select>

					<label>{{ $t('projects.assigned-to') }}</label>
					<v-menu :close-on-click="true" :close-on-content-click="false" offset-y :disabled="isReadOnly">
						<template v-slot:activator="{ on }">
							<button type="button" v-on="on" class="assignee-field" :disabled="isReadOnly">
								<span v-if="deliverable.assignedToList.length" class="assignee-stack">
									<assigned-user
										v-for="(assignedTo, index) in deliverable.assignedToList"
										:key="assignedTo"
										:assigned-to="assignedTo"
										:show-name="false"
										:assignable-users="assignableUsers"
										empty-label="--"
										:style="{ zIndex: deliverable.assignedToList.length - index }"
									></assigned-user>
								</span>
								<span v-else class="empty-value">—</span>
								<portal-icon name="chevron-down" :size="15"></portal-icon>
							</button>
						</template>
						<div class="assignee-menu">
							<label v-for="user in assignableUsers" :key="user.userId" class="assignee-option">
								<input type="checkbox" :value="user.userId" v-model="deliverable.assignedToList" />
								<assigned-user
									v-if="user.userId"
									:assigned-to="user.userId"
									:show-name="true"
									:assignable-users="assignableUsers"
								></assigned-user>
							</label>
						</div>
					</v-menu>

					<label>{{ $t('projects.start') }}</label>
					<date-selector
						:date="deliverable.startDate"
						:label="null"
						:panel="true"
						@change="deliverable.startDate = $event"
						:disabled="isReadOnly"
					/>

					<label>{{ $t('projects.due') }}</label>
					<date-selector
						:date="deliverable.dueDate"
						:label="null"
						:panel="true"
						@change="deliverable.dueDate = $event"
						:disabled="isReadOnly"
					/>
				</div>

				<div class="panel-section">
					<h2>{{ $t('projects.description') }}</h2>
					<div class="description-editor">
						<markdown
							:value="deliverable.description"
							:id="deliverable.id"
							:placeholder="$t('projects.description-placeholder')"
							:input-only-mode="false"
							:solo="true"
							:flat="true"
							@update="deliverable.description = $event"
							:disabled="isReadOnly"
						/>
					</div>
				</div>

				<div class="panel-section">
					<h2>
						{{ $t('projects.tasks') }}
						<span v-if="deliverable.tasks && deliverable.tasks.length" class="section-count">
							{{ completedTaskCount }}/{{ deliverable.tasks.length }}
						</span>
					</h2>
					<div class="checklist-shell">
						<tasks ref="tasks" :deliverable="deliverable" :detail-view="true" :disabled="isReadOnly"></tasks>
					</div>
				</div>

				<div class="approval-card" v-if="deliverable.approvalRequired">
					<div>
						<strong>{{ $t('projects.client-workflow') }}</strong>
						<span v-if="deliverable.approvalRequestedAt">
							{{ $t('projects.submitted') }}
							{{ DateTime.fromISO(deliverable.approvalRequestedAt).toFormat('DD') }}
						</span>
					</div>
					<button v-if="!isProjectSuspended(project)" type="button" class="approval-action" @click="confirmApproval">
						{{ $t('global.ok') }}
						<portal-icon name="check" :size="15"></portal-icon>
					</button>
				</div>

				<div class="panel-section" v-if="approvals.length">
					<h2>{{ $t('projects.client-workflow') }}</h2>
					<ul class="approval-history">
						<li v-for="approval in approvals" :key="approval.id">
							{{ approval.approverName }} {{ $t('global.on') }}
							{{ DateTime.fromISO(approval.approvedAt).toFormat('DD') }} {{ $t('global.from') }} [{{
								approval.approvalStatus
							}}] {{ $t('global.status') }}
						</li>
					</ul>
				</div>
			</section>

			<section v-if="panelSubtasks.length" v-show="tabItem === 'subtasks'" class="task-section">
				<div class="subtask-list">
					<button
						v-for="subtask in panelSubtasks"
						:key="subtask.id"
						type="button"
						class="subtask-row"
						:disabled="isNavigating"
						@click="openSubtask(subtask)"
					>
						<span
							class="subtask-status-dot"
							:style="{ backgroundColor: statusForSubtask(subtask).hexColor || 'var(--portal-muted)' }"
						></span>
						<div class="subtask-copy">
							<strong>{{ subtask.name }}</strong>
							<div class="subtask-meta">
								<span>{{ statusForSubtask(subtask).label || $t('projects.no-status') }}</span>
								<span v-if="subtask.dueDate" class="subtask-due">
									<portal-icon name="calendar-days" :size="13"></portal-icon>
									{{ DateTime.fromISO(subtask.dueDate).toLocaleString(DateTime.DATE_MED) }}
								</span>
							</div>
						</div>
						<portal-icon class="subtask-open-icon" name="chevron-right" :size="17"></portal-icon>
					</button>
				</div>
			</section>

			<section v-show="tabItem === 'comments'" class="task-section comments-section">
				<activity ref="activity" :project="project" :deliverable="deliverable" :project-service="projectService" />
			</section>

			<section v-if="tabItem === 'files'" id="deliverable-files" class="task-section files-section">
				<domain-root
					type="Task"
					:id="deliverable.id"
					:show-deleted="showDeleted"
					:read-only="isReadOnly"
					:allow-upload="canUploadFiles"
				></domain-root>
			</section>
		</main>

		<footer class="task-panel-footer" v-if="!isReadOnly">
			<button type="button" class="delete-action" @click="confirmDeleteDeliverable">
				<portal-icon name="trash-2" :size="15"></portal-icon>
				{{ $t('global.delete') }}
			</button>
		</footer>
	</div>
</template>

<script>
	import { isProjectSuspended, canEditProject } from '@/modules/projects/projectLifecycle';
	import Activity from '@/modules/projects/deliverable/Activity';
	import Tasks from '@/modules/projects/deliverable/Tasks';
	import { DateTime } from 'luxon';
	import ProjectService from '@/modules/projects/ProjectService';
	import marked from 'marked';
	import ConfirmModal from '@/components/ConfirmModal';
	import Markdown from '@/components/Markdown';
	import DateSelector from '@/components/DateSelector';
	import jsonPatch from 'fast-json-patch';
	import AssignedUser from '@/components/AssignedUser';
	import DomainRoot from '@/modules/files/DomainRoot';
	import PortalIcon from '@/components/PortalIcon.vue';

	export default {
		name: 'DeliverableDetail',

		props: ['project', 'origDeliverable', 'statusList', 'subtasks'],

		components: {
			Tasks,
			Activity,
			Markdown,
			DateSelector,
			AssignedUser,
			DomainRoot,
			PortalIcon,
		},

		data: function() {
			return {
				tabItem: 'overview',
				deliverable: JSON.parse(JSON.stringify(this.origDeliverable)),
				observer: null,
				isSaving: false,
				DateTime: DateTime,
				projectService: new ProjectService(),
				assignableUsers: [],
				activeSubtasks: JSON.parse(JSON.stringify(this.subtasks || this.origDeliverable.subtasks || [])),
				navigationStack: [],
				isNavigating: false,
				showDeleted: false,
			};
		},

		mounted() {
			this.observer = jsonPatch.observe(this.deliverable);
			this.$store.state.eventBus.$on(this.getChannelName(), this.eventHandler);
			if (!this.isReadOnly) {
				this.getAssignableUsers();
			}
		},

		beforeDestroy() {
			this.$store.state.eventBus.$off(this.getChannelName(), this.eventHandler);
		},

		methods: {
			isProjectSuspended, canEditProject,
			handleClose: function() {
				this.$emit('result', this.rootDeliverable());
			},

			getAssignableUsers: function() {
				this.projectService.getAssignableUsers(this.project.id).then((res) => {
					this.assignableUsers.splice(0, this.assignableUsers.length);
					this.assignableUsers.push(...res.data);
				});
			},

			statusForSubtask: function(subtask) {
				return this.statusList.find((status) => status.id === subtask.statusId) || {};
			},

			rootDeliverable: function() {
				return this.navigationStack.length ? this.navigationStack[0].deliverable : this.deliverable;
			},

			channelNameFor: function(deliverableId) {
				return this.$store.getters.getMessageChannelPrefix + '.pr-' + this.project.id + '.d-' + deliverableId;
			},

			openSubtask: function(subtask) {
				if (this.isNavigating) {
					return;
				}

				this.isNavigating = true;
				this.projectService
					.getDeliverableDetail(subtask.id)
					.then((res) => {
						this.$store.state.eventBus.$off(this.getChannelName(), this.eventHandler);
						this.navigationStack.push({
							deliverable: this.deliverable,
							observer: this.observer,
							subtasks: this.activeSubtasks,
							tabItem: 'subtasks',
						});
						this.deliverable = JSON.parse(JSON.stringify(res.data));
						this.activeSubtasks = JSON.parse(JSON.stringify(subtask.subtasks || []));
						this.observer = jsonPatch.observe(this.deliverable);
						this.tabItem = 'overview';
						this.$store.state.eventBus.$on(this.getChannelName(), this.eventHandler);
					})
					.catch((err) => {
						this.$store.commit('error', err.response && err.response.data ? err.response.data.message : err.message);
					})
					.finally(() => {
						this.isNavigating = false;
					});
			},

			updateParentSubtask: function(parentState) {
				const subtaskIndex = parentState.subtasks.findIndex((subtask) => subtask.id === this.deliverable.id);
				if (subtaskIndex > -1) {
					parentState.subtasks.splice(subtaskIndex, 1, {
						...parentState.subtasks[subtaskIndex],
						...this.deliverable,
					});
				}
			},

			navigateBack: function() {
				if (!this.navigationStack.length || this.isSaving || this.isNavigating) {
					return;
				}

				this.persistCurrentChanges()
					.then(() => {
						this.$store.state.eventBus.$off(this.getChannelName(), this.eventHandler);
						const parentState = this.navigationStack.pop();
						this.updateParentSubtask(parentState);
						this.deliverable = parentState.deliverable;
						this.observer = parentState.observer;
						this.activeSubtasks = parentState.subtasks;
						this.tabItem = parentState.tabItem;
						this.$store.state.eventBus.$on(this.getChannelName(), this.eventHandler);
					})
					.catch(() => {});
			},

			checkClear: function(propName) {
				if (this.deliverable[propName] === '--clear--') {
					this.deliverable[propName] = null;
				}
			},

			eventHandler: function(event) {
				if (event.userMetadata === 'Comment') {
					this.handleCommentEvent(event);
				}
			},

			getChannelName() {
				return this.channelNameFor(this.deliverable.id);
			},

			handleCommentEvent(event) {
				event.message.timestamp = new Date().toISOString().split('.')[0] + 'Z';
				let comment = event.message;
				console.log(comment);

				if (comment.edited) {
					let ix = this.deliverable.comments.findIndex((c) => c.id === comment.id);
					if (ix > -1) {
						if (comment.comment === '_deleted_') {
							this.deliverable.comments.splice(ix, 1);
						} else {
							this.deliverable.comments.splice(ix, 1, comment);
						}
					}
				} else {
					this.deliverable.comments.push(comment);
				}
			},

			saveAndCloseInternal: function() {
				this.persistCurrentChanges()
					.then(() => {
						if (this.navigationStack.length) {
							this.updateParentSubtask(this.navigationStack[this.navigationStack.length - 1]);
						}
						this.$emit('result', this.rootDeliverable());
					})
					.catch(() => {});
			},

			persistCurrentChanges: function() {
				if (this.$refs.tasks) {
					this.$refs.tasks.clearEmpty();
				}
				const pendingComment = this.$refs.activity
					? this.$refs.activity.checkCommentBeforeSave()
					: Promise.resolve();
				return pendingComment.then(() => {
					this.isSaving = true;
					const patch = this.generatePatchFiltered();

					if (patch.length > 0) {
						this.$store.commit('startLoading');
						return this.save(patch)
							.then(() => this.deliverable)
							.finally(() => {
								this.$store.commit('stopLoading');
								this.isSaving = false;
							});
					} else {
						this.isSaving = false;
						return this.deliverable;
					}
				});
			},

			generatePatchFiltered: function() {
				const patch = jsonPatch.generate(this.observer, false);
				if (patch.length > 0) {
					for (let i = patch.length - 1; i >= 0; i--) {
						let path = patch[i].path;
						if (
							path.startsWith('/comments/') ||
							path.startsWith('/files/') ||
							path.startsWith('/events/') ||
							path.startsWith('/product')
						) {
							patch.splice(i, 1);
						}
					}
					return patch;
				} else {
					return [];
				}
			},

			save: function(patch) {
				if (this.$refs.tasks) {
					this.$refs.tasks.clearEmpty();
				}
				let result = this.projectService.patchDeliverable(this.deliverable.id, patch);

				return result
					.then((res) => {
						return Promise.resolve(res);
					})
					.catch((err) => {
						this.$store.commit('error', err.response.data.message);
						return Promise.reject(err);
					});
			},

			confirmDeleteDeliverable: function() {
				let binding = {
					headingText: this.$t('global.confirm'),
					bodyText: this.$t('projects.delete-deliverable'),
					yesText: this.$t('global.delete'),
					noText: this.$t('global.cancel'),
				};
				this.$store.state.globalModalController.openModal(ConfirmModal, binding, false, false).then((confirm) => {
					if (confirm) {
						this.projectService.deleteDeliverable(this.deliverable.id).then(() => {
							this.deliverable.deleted = true;
							if (this.navigationStack.length) {
								const parentState = this.navigationStack[this.navigationStack.length - 1];
								parentState.subtasks = parentState.subtasks.filter(
									(subtask) => subtask.id !== this.deliverable.id
								);
							}
							this.$emit('result', this.rootDeliverable());
						});
					}
				});
			},

			confirmApproval: function() {
				let binding = {
					icon: '$success',
					severity: 'success',
					headingText: this.$t('projects.submit'),
					bodyText: this.$t('projects.confirm-approval'),
					yesText: this.$t('global.submit'),
					noText: this.$t('global.cancel'),
					noIsSuperAction: false,
				};
				this.$store.state.globalModalController.openModal(ConfirmModal, binding, false, false).then((confirm) => {
					if (confirm) {
						const pendingComment = this.$refs.activity
							? this.$refs.activity.checkCommentBeforeSave()
							: Promise.resolve();
						pendingComment.finally(() => {
							this.projectService.approveDeliverable(this.project.id, this.deliverable.id).then((res) => {
								this.deliverable = res.data;
							});
						});
					}
				});
			},

			formatMarkdown: function(comment) {
				if (comment) {
					let m = marked(comment, { breaks: true });
					m = m.replaceAll('<a href', '<a target="_blank" href');
					return m;
				} else {
					return '--';
				}
			},

			formatTime: function(number, type) {
				if (number < 0) {
					number = number * -1;
					return number + ' ' + type + (number > 1 ? 's' : '') + ' ' + this.$t('global.ago');
				} else {
					return number + ' ' + type + (number > 1 ? 's' : '') + ' ' + this.$t('global.remaining');
				}
			},
		},

		computed: {
			isReadOnly: function() {
				return !canEditProject(this.project);
			},

			canUploadFiles: function() {
				if (isProjectSuspended(this.project)) return false;
				return (
					this.project.portalAccess === 'Full access' ||
					this.project.portalAccess === 'Read only' ||
					this.deliverable.approvalRequired ||
					this.approvals.length > 0
				);
			},

			completedTaskCount: function() {
				return (this.deliverable.tasks || []).filter((task) => task.complete).length;
			},

			panelSubtasks: function() {
				return this.activeSubtasks;
			},

			visibleTabCount: function() {
				return this.panelSubtasks.length ? 4 : 3;
			},

			approvals: function() {
				if (this.deliverable.approvals && this.deliverable.approvals.length) {
					let result = [...this.deliverable.approvals];
					return result.reverse();
				} else {
					return [];
				}
			},
		},

		watch: {},
	};
</script>

<style scoped lang="scss">
	.task-panel {
		width: 100%;
		height: 100%;
		min-height: 0;
		display: flex;
		flex-direction: column;
		background: var(--portal-surface);
		color: var(--portal-text);
	}

	.task-panel-header {
		min-height: 58px;
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 12px 14px 8px 22px;
	}

	.task-title-input {
		min-width: 0;
		flex: 1;
		padding: 3px 0;
		border: 0;
		outline: 0;
		background: transparent;
		color: var(--portal-text);
		font-family: var(--primary-font);
		font-size: 16.5px;
		font-weight: 750;
		letter-spacing: -0.012em;
	}

	.task-title-input::placeholder {
		color: var(--portal-muted);
		font-weight: 600;
	}

	.task-title-input:disabled {
		opacity: 1;
	}

	.task-panel-actions {
		flex: 0 0 auto;
		display: flex;
		align-items: center;
		gap: 3px;
	}

	.save-action,
	.icon-action,
	.task-tabs button,
	.assignee-field,
	.approval-action,
	.delete-action {
		font-family: var(--primary-font);
		cursor: pointer;
	}

	.save-action {
		height: 32px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		padding: 0 12px;
		border: 0;
		border-radius: var(--brand-button-radius, 9px);
		background: var(--v-primary-base);
		color: var(--v-white-base);
		font-size: 12.5px;
		font-weight: 700;
	}

	.save-action:disabled {
		cursor: wait;
		opacity: 0.65;
	}

	.icon-action {
		width: 32px;
		height: 32px;
		display: grid;
		place-items: center;
		padding: 0;
		border: 0;
		border-radius: var(--brand-button-radius, 8px);
		background: transparent;
		color: var(--portal-muted);
	}

	.icon-action:hover {
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.back-action {
		margin-left: -8px;
	}

	.save-action:focus-visible,
	.icon-action:focus-visible,
	.task-tabs button:focus-visible,
	.assignee-field:focus-visible,
	.approval-action:focus-visible,
	.delete-action:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: 2px;
	}

	.task-panel-fixed {
		flex: 0 0 auto;
		padding: 0 22px 12px;
		border-bottom: 1px solid var(--portal-border);
	}

	.project-context {
		display: flex;
		align-items: center;
		gap: 7px;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 600;
	}

	.project-context span {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.project-context .parent-context {
		color: var(--portal-text);
	}

	.task-tabs {
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 3px;
		margin-top: 12px;
		padding: 3px;
		border-radius: var(--brand-button-radius, 11px);
		background: var(--v-gray_20-base);
	}

	.task-tabs button {
		min-width: 0;
		height: 30px;
		padding: 0 8px;
		overflow: hidden;
		border: 0;
		border-radius: var(--brand-button-radius, 8px);
		background: transparent;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 650;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.task-tabs button.active {
		background: var(--portal-surface);
		color: var(--portal-text);
		box-shadow: 0 1px 4px rgba(16, 17, 15, 0.1);
	}

	.tab-count {
		margin-left: 4px;
		color: var(--portal-muted);
		font-size: 10px;
	}

	.task-panel-body {
		min-height: 0;
		flex: 1 1 auto;
		overflow-y: auto;
		padding: 18px 22px 32px;
	}

	.task-section {
		min-width: 0;
	}

	.meta-grid {
		display: grid;
		grid-template-columns: 92px minmax(0, 1fr);
		align-items: center;
		gap: 8px 14px;
	}

	.meta-grid > label {
		color: var(--portal-muted);
		font-size: 12.5px;
		font-weight: 600;
	}

	.panel-field {
		min-width: 0;
	}

	.panel-field ::v-deep .v-input__slot {
		min-height: 38px !important;
		margin: 0;
		padding: 0 11px !important;
		border: 0 !important;
		border-radius: var(--brand-field-radius, 10px) !important;
		background: var(--v-gray_20-base) !important;
		box-shadow: none !important;
	}

	.panel-field ::v-deep .v-input__slot::before,
	.panel-field ::v-deep .v-input__slot::after {
		display: none !important;
	}

	.panel-field ::v-deep input,
	.panel-field ::v-deep .v-select__selection {
		color: var(--portal-text);
		font-size: 13px;
	}

	.panel-field ::v-deep .v-input__append-inner {
		align-self: center;
		margin-top: 0;
	}

	.status-chip {
		max-width: 100%;
		display: inline-flex;
		align-items: center;
		gap: 7px;
		overflow: hidden;
		color: var(--portal-text);
		font-size: 12.5px;
		font-weight: 650;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.status-chip span {
		width: 8px;
		height: 8px;
		flex: 0 0 8px;
		border-radius: 999px;
		background: var(--status-color);
	}

	.assignee-field {
		width: 100%;
		height: 38px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		padding: 0 11px;
		border: 0;
		border-radius: var(--brand-field-radius, 10px);
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
	}

	.assignee-field:disabled {
		cursor: default;
	}

	.assignee-stack {
		display: flex;
		align-items: center;
	}

	.assignee-stack > * + * {
		margin-left: -7px;
	}

	.assignee-stack ::v-deep img,
	.assignee-stack ::v-deep .avatar {
		border: 2px solid var(--v-gray_20-base);
	}

	.assignee-menu {
		min-width: 250px;
		max-height: 320px;
		padding: 8px;
		overflow-y: auto;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 12px);
		background: var(--portal-surface);
		box-shadow: 0 16px 40px rgba(16, 17, 15, 0.16);
	}

	.assignee-option {
		min-height: 38px;
		display: flex;
		align-items: center;
		gap: 9px;
		padding: 5px 7px;
		border-radius: var(--brand-button-radius, 8px);
		cursor: pointer;
	}

	.assignee-option:hover {
		background: var(--v-gray_20-base);
	}

	.assignee-option input {
		width: 16px;
		height: 16px;
		accent-color: var(--v-primary-base);
	}

	.panel-section {
		margin-top: 24px;
		padding-top: 18px;
		border-top: 1px solid var(--portal-border);
	}

	.task-panel .panel-section > h2 {
		display: flex;
		align-items: center;
		gap: 7px;
		margin: 0 0 11px;
		color: var(--portal-muted);
		font-size: 10.5px;
		font-weight: 750;
		letter-spacing: 0.075em;
		text-transform: uppercase;
	}

	.section-count {
		padding: 1px 6px;
		border-radius: 999px;
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
		font-size: 10px;
		letter-spacing: 0;
	}

	.description-editor {
		min-height: 96px;
		padding: 11px 12px;
		border-radius: var(--brand-field-radius, 11px);
		background: var(--v-gray_20-base);
		color: var(--portal-text);
		font-size: 13px;
	}

	.description-editor ::v-deep .v-input__slot {
		margin: 0;
		border-radius: var(--brand-field-radius, 8px) !important;
		background: transparent !important;
		box-shadow: none !important;
	}

	.description-editor ::v-deep textarea {
		color: var(--portal-text);
		font-size: 13px;
		line-height: 1.55;
	}

	.description-editor ::v-deep .marked {
		min-height: 72px;
		line-height: 1.55;
	}

	.checklist-shell {
		padding: 3px 4px 5px;
		border-radius: var(--brand-surface-radius, 11px);
		background: var(--v-gray_20-base);
	}

	.checklist-shell ::v-deep .task-item-detail {
		min-height: 38px;
		align-items: center;
		padding: 4px 6px;
		border-radius: var(--brand-surface-radius, 8px);
	}

	.checklist-shell ::v-deep .task-item-detail:hover {
		background: var(--portal-surface);
	}

	.checklist-shell ::v-deep .edit-content {
		color: var(--portal-text);
		font-size: 13px;
	}

	.checklist-shell ::v-deep .add-full {
		padding: 7px 8px;
		color: var(--portal-muted);
		font-size: 12.5px;
	}

	.approval-card {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 12px;
		margin-top: 22px;
		padding: 12px;
		border: 1px solid color-mix(in srgb, var(--v-success-base) 30%, var(--portal-border));
		border-radius: var(--brand-surface-radius, 11px);
		background: color-mix(in srgb, var(--v-success-base) 7%, var(--portal-surface));
	}

	.approval-card > div {
		min-width: 0;
		display: grid;
		gap: 2px;
	}

	.approval-card strong {
		font-size: 12.5px;
	}

	.approval-card span {
		color: var(--portal-muted);
		font-size: 11px;
	}

	.approval-action {
		height: 32px;
		display: inline-flex;
		align-items: center;
		gap: 5px;
		padding: 0 10px;
		border: 0;
		border-radius: var(--brand-button-radius, 9px);
		background: var(--v-success-base);
		color: var(--v-white-base);
		font-size: 12px;
		font-weight: 700;
	}

	.approval-history {
		display: grid;
		gap: 8px;
		margin: 0;
		padding-left: 18px;
		color: var(--portal-muted);
		font-size: 12px;
		line-height: 1.5;
	}

	.subtask-list {
		display: grid;
		gap: 7px;
	}

	.subtask-row {
		width: 100%;
		min-height: 58px;
		display: grid;
		grid-template-columns: 9px minmax(0, 1fr) 18px;
		align-items: center;
		gap: 10px;
		padding: 10px 12px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 11px);
		background: var(--portal-surface);
		color: var(--portal-text);
		font-family: var(--primary-font);
		text-align: left;
		cursor: pointer;
		transition: border-color 150ms ease, background-color 150ms ease, box-shadow 150ms ease;
	}

	.subtask-row:hover {
		border-color: color-mix(in srgb, var(--v-primary-base) 24%, var(--portal-border));
		background: color-mix(in srgb, var(--v-primary-base) 3%, var(--portal-surface));
		box-shadow: 0 5px 16px rgba(16, 17, 15, 0.06);
	}

	.subtask-row:focus-visible {
		outline: 2px solid var(--v-primary-base);
		outline-offset: 2px;
	}

	.subtask-row:disabled {
		cursor: wait;
		opacity: 0.65;
	}

	.subtask-status-dot {
		width: 9px;
		height: 9px;
		border-radius: 999px;
	}

	.subtask-copy {
		min-width: 0;
		display: grid;
		gap: 4px;
		text-align: left;
	}

	.subtask-copy strong {
		overflow: hidden;
		color: var(--portal-text);
		font-size: 13px;
		font-weight: 650;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.subtask-meta,
	.subtask-meta span,
	.subtask-due {
		color: var(--portal-muted);
		font-size: 11px;
	}

	.subtask-meta {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 6px 12px;
	}

	.subtask-due {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		white-space: nowrap;
	}

	.subtask-open-icon {
		color: var(--portal-muted);
	}

	.comments-section ::v-deep .comment-box {
		padding: 11px 12px;
		border: 0;
		border-radius: var(--brand-surface-radius, 11px);
		background: var(--v-gray_20-base);
	}

	.comments-section ::v-deep .v-input__slot {
		border-radius: var(--brand-field-radius, 10px) !important;
		background: var(--v-gray_20-base) !important;
		box-shadow: none !important;
	}

	.files-section {
		min-height: 320px;
	}

	.task-panel-footer {
		flex: 0 0 auto;
		padding: 10px 16px calc(10px + env(safe-area-inset-bottom));
		border-top: 1px solid var(--portal-border);
	}

	.delete-action {
		height: 32px;
		display: inline-flex;
		align-items: center;
		gap: 6px;
		padding: 0 9px;
		border: 0;
		border-radius: var(--brand-button-radius, 8px);
		background: transparent;
		color: var(--v-error-base);
		font-size: 12px;
		font-weight: 650;
	}

	.delete-action:hover {
		background: color-mix(in srgb, var(--v-error-base) 8%, transparent);
	}

	.empty-value {
		color: var(--portal-muted);
	}

	@media (max-width: 599px) {
		.task-panel-header {
			padding-left: 18px;
		}

		.task-panel-fixed {
			padding-right: 18px;
			padding-left: 18px;
		}

		.task-panel-body {
			padding: 16px 18px 28px;
		}

		.meta-grid {
			grid-template-columns: 78px minmax(0, 1fr);
			column-gap: 10px;
		}

		.task-tabs button {
			padding-right: 4px;
			padding-left: 4px;
			font-size: 11px;
		}

		.subtask-row {
			padding-right: 10px;
			padding-left: 10px;
		}
	}
</style>

<style lang="scss">
	.task-status-menu.v-menu__content {
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 11px);
		background: var(--portal-surface);
		box-shadow: 0 14px 36px rgba(16, 17, 15, 0.16);
	}

	.task-status-menu .v-list {
		padding: 6px;
		background: var(--portal-surface);
	}

	.task-status-menu .v-list-item {
		min-height: 38px;
		padding: 0 9px;
		border-radius: var(--brand-button-radius, 8px);
	}

	.task-status-menu .v-list-item + .v-list-item {
		margin-top: 2px;
	}

	.task-status-menu .v-list-item--active {
		background: color-mix(in srgb, var(--v-primary-base) 8%, var(--portal-surface));
		color: var(--portal-text) !important;
	}

	.task-status-menu .v-list-item--active::before {
		opacity: 0;
	}
</style>
